import { dynamic } from '@unifyapps/carbon/external/dynamic';
import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import useCustomStyles from '@unifyapps/carbon/utils/useCustomStyles';
import type {
  ButtonBlockStateType,
  ButtonComponentType,
  ButtonCustomStyles,
  ComputedButtonBlockStateType,
} from '@unifyapps/defs/types/blocks';
import React, { memo, useMemo } from 'react';
import { useBlockTooltip } from '../common/hooks/useBlockTooltip';
import { useBlockInteractions } from '../hooks/useBlockInteractions';
import BaseButton from './components/BaseButton';
import { useButtonPreConfiguredEvents } from './useButtonPreConfiguredEvents';
import { normalizeButtonTooltip } from './utils/normalizeButtonTooltip';

const importReCaptchaButtonComponent = () =>
  import(
    /* webpackChunkName: "ReCaptchaButton" */
    './components/ReCaptchaButton'
  );

const ReCaptchaButton = dynamic(importReCaptchaButtonComponent);

function Button({
  attributesAndEventListeners,
  events,
  computedBlockState,
  updateBlockState,
  dndRef,
  block,
  component,
  registry,
}: BlockComponentProps<ButtonComponentType, ButtonBlockStateType, ComputedButtonBlockStateType>) {
  const { content, appearance } = computedBlockState;
  const { slots } = component;
  const BlockRenderer = registry.getBlockRenderer();
  const buttonPreConfiguredEvents = useButtonPreConfiguredEvents(computedBlockState);
  const _events = useMemo(
    () => [...buttonPreConfiguredEvents, ...(events ?? [])],
    [events, buttonPreConfiguredEvents],
  );

  const { addOns, value, contentMode } = content;
  const { reCaptcha } = addOns ?? {};
  const tooltipAddOn = useMemo(() => normalizeButtonTooltip(addOns), [addOns]);
  const { reCaptchaSiteKey } = reCaptcha ?? {};

  // used to check if we need recaptcha in our button
  const isCaptchaNeeded = Boolean(reCaptchaSiteKey);

  // in BUTTON, we are only handling onClick event, for any new event handling handle it for recaptcha too
  const { emitEvent } = useBlockEvents(_events, computedBlockState.id);

  const { onClick, onMouseEnter, longPressProps } = useBlockInteractions({
    events: _events,
    emitEvent,
    blockType: 'Button',
  });

  const { className, style } = useBlockAppearanceStyles({
    appearanceStyles: appearance?.styles,
  });
  const { finalStyle } = useCustomStyles({
    customCSS: block?.additional?.customCSS,
    customStyles: block?.component?.appearance?.customStyles,
    style,
  });

  const type = content.type ?? 'default';
  const size = appearance?.size ?? 'md';

  const contentSlotNode = useMemo(
    () =>
      slots?.content?.blockId ? (
        <BlockRenderer blockId={slots.content.blockId} parentBlockId={computedBlockState.id} />
      ) : null,
    [slots?.content?.blockId, computedBlockState.id, BlockRenderer],
  );

  const baseButtonProps = useMemo(
    () => ({
      appearance,
      className,
      attributesAndEventListeners,
      onClick,
      onMouseEnter,
      size,
      customStyle: finalStyle as ButtonCustomStyles,
      type,
      value,
      longPressProps,
      contentSlotNode,
      contentMode,
    }),
    [
      appearance,
      className,
      attributesAndEventListeners,
      onClick,
      finalStyle,
      onMouseEnter,
      size,
      type,
      value,
      longPressProps,
      contentSlotNode,
      contentMode,
    ],
  );

  const { wrapWithTooltip, tooltipBuilderNode } = useBlockTooltip({
    blockId: computedBlockState.id,
    tooltipAddOn,
    tooltipSlotBlockId: slots?.tooltip?.blockId,
    BlockRenderer,
  });

  // Suspense can't forward a ref or the hover/focus props the tooltip wrapper injects, so
  // wrapping has to happen inside ReCaptchaButton itself (around its own BaseButton, past the
  // suspense boundary)
  const buttonElement = isCaptchaNeeded ? (
    <React.Suspense fallback={<BaseButton {...baseButtonProps} onClick={undefined} />}>
      <ReCaptchaButton
        baseButtonProps={baseButtonProps}
        emitEvent={emitEvent}
        reCaptcha={content.addOns?.reCaptcha}
        reCaptchaToken={computedBlockState.reCaptchaToken}
        ref={dndRef}
        updateBlockState={updateBlockState}
        wrapWithTooltip={wrapWithTooltip}
      />
    </React.Suspense>
  ) : (
    wrapWithTooltip(<BaseButton {...baseButtonProps} ref={dndRef} />)
  );

  return (
    <>
      {buttonElement}
      {tooltipBuilderNode}
    </>
  );
}

export default memo(Button);
